import { Controller } from "@hotwired/stimulus"
import { createPrivyClient } from "../lib/privy_client"
import { workStatus, workError, setBusy } from "../lib/work_status"

// Plain-language copy (R16, R5): expectations are stated before anyone taps,
// and a failure always offers the connect-a-wallet fallback (AE3).
export const COPY = {
  opening: "Getting your top-up ready…",
  opened: "Finish in the card partner's tab. Money usually arrives in your wallet within a few minutes, and we'll say so here the moment it does.",
  failed: "We couldn't start a card top-up. It may not be available where you are. You can try again, or connect a wallet you already have instead.",
  arrived: (amount) => `Your top-up arrived. ${amount} is in your wallet.`
}

// The chain is the only honest signal that money landed (the card partner's
// tab never reports back to us), so after opening the checkout we watch the
// wallet: poll the balance and announce the first increase.
const POLL_INTERVAL_MS = 10_000
const POLL_DEADLINE_MS = 15 * 60 * 1000

// The stages of a card top-up, as the step line names them: the checkout is
// signed and opened, the saver pays in the partner's tab, the money lands.
export const STEPS = [ "Getting ready", "Card partner", "In your wallet" ]

// Card top-up for the provisioned wallet (KTD6): ask Privy's funding API for
// a signed MoonPay checkout URL that delivers USDC on Solana to this wallet,
// and open it in its own tab. MoonPay runs its own identity check in that tab;
// nothing about it passes through our server.
export default class extends Controller {
  static targets = [ "button", "status", "error", "fallback" ]
  static values = { appId: String, sdkUrl: String, address: String, sandbox: Boolean, balanceUrl: String }

  connect() {
    this.element.dataset.privyFundingReady = "true"
  }

  disconnect() {
    this.stopWatching()
    if (this.teardown) this.teardown()
  }

  async addFunds() {
    setBusy(this.buttonTarget, true)
    this.errorTarget.hidden = true
    this.setStatus(COPY.opening, "working", 0)

    // Opened before any awaiting so popup blockers see a user gesture; the
    // checkout URL lands in it once signed.
    const checkoutTab = window.open("about:blank", "_blank")
    try {
      const { privy } = await this.client()
      // MoonPay's sandbox has no USDC_SOL (supportsTestMode: false) and shows a
      // misleading region error for it; SOL is test-mode enabled, so sandbox
      // runs exercise the checkout with SOL while live mode buys real USDC.
      const { signedUrl } = await privy.funding.moonpay.sign({
        address: this.addressValue,
        useSandbox: this.sandboxValue,
        config: { currencyCode: this.sandboxValue ? "SOL" : "USDC_SOL" }
      })
      if (checkoutTab) {
        checkoutTab.location = signedUrl
      } else {
        window.location.assign(signedUrl)
      }
      // The saver's move now, in the partner's tab; we watch the wallet.
      this.setStatus(COPY.opened, "waiting", 1)
      this.watchArrival()
    } catch (error) {
      console.debug(`[${this.identifier}] top-up failed`, error)
      if (checkoutTab) checkoutTab.close()
      this.clientPromise = null
      this.setStatus("")
      workError(this.errorTarget, COPY.failed)
      this.fallbackTarget.hidden = false
    } finally {
      setBusy(this.buttonTarget, false)
    }
  }

  client() {
    this.clientPromise ||= createPrivyClient({
      sdkUrl: this.sdkUrlValue,
      appId: this.appIdValue,
      container: this.element
    }).then((client) => {
      this.teardown = client.teardown
      return client
    })
    return this.clientPromise
  }

  async watchArrival() {
    if (!this.hasBalanceUrlValue) return
    this.stopWatching()

    const baseline = await this.balances()
    if (!baseline) return // can't read the wallet right now; the copy already set expectations

    const startedAt = Date.now()
    this.pollTimer = setInterval(async () => {
      if (Date.now() - startedAt > POLL_DEADLINE_MS) {
        // No longer watching, so the line stops breathing; the words stand.
        this.setStatus(COPY.opened, "idle")
        return this.stopWatching()
      }

      const now = await this.balances()
      if (!now) return

      const usdcDelta = now.usdc - baseline.usdc
      const solDelta = now.sol - baseline.sol
      if (usdcDelta <= 0 && solDelta <= 0) return

      const amount = usdcDelta > 0
        ? `$${usdcDelta.toFixed(2)} USDC`
        : `${solDelta.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")} SOL`
      this.setStatus(COPY.arrived(amount), "done", 2)
      this.stopWatching()
    }, POLL_INTERVAL_MS)
  }

  stopWatching() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async balances() {
    try {
      const response = await fetch(this.balanceUrlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) return null
      const data = await response.json()
      return { usdc: Number(data.usdc), sol: Number(data.sol) }
    } catch {
      return null
    }
  }

  // The status line and its step line (lib/work_status.js); an empty line
  // hides, with no step line.
  setStatus(text, state = "idle", step = null) {
    workStatus(this.statusTarget, text, text === "" ? "idle" : state, step === null ? {} : { steps: STEPS, step })
    this.statusTarget.hidden = text === ""
  }
}
